import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { TrackImageInput } from '@/types'

import { Carousel, Grid, More, Wrapper, ProductImage } from './ProductGallery.styles'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import { Close, Left, Overlay, PopupImage, Right } from '@big-red-group/storefront-common-ui'

type ProductGalleryProps = {
  images?: string[]
  trackImageEvent: (args: TrackImageInput) => void
}

// TODO mobile version
// TODO fix overlay scroll
const ProductGallery: React.FC<ProductGalleryProps> = ({ images, trackImageEvent }) => {
  const clickedFrom = 'Product Gallery'
  const [showPopup, setShowPopup] = useState(false)
  const [currentImage, setCurrentImage] = useState<number>(0)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowPopup(false)
        return
      }

      if (event.key === 'ArrowLeft') {
        setCurrentImage(currentImage === 0 ? images!.length - 1 : currentImage! - 1)
        return
      }

      if (event.key === 'ArrowRight') {
        setCurrentImage(currentImage === images!.length - 1 ? 0 : currentImage! + 1)
        return
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [setCurrentImage, currentImage, images])

  useEffect(() => {
    document.body.style.overflow = showPopup ? 'hidden' : 'unset'
  }, [showPopup])

  const handleImageFullyLoaded = (e: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    // Here we have to track successful "Image Viewed" event
    trackImageEvent({
      position: currentImage + 1,
      action: 'Viewed',
      image: e.currentTarget,
      clickedFrom,
    })
  }

  const handleImageLoadError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Here we have to track failed "Image Viewed" event
    trackImageEvent({
      position: currentImage + 1,
      action: 'Viewed',
      image: e.currentTarget,
      success: false,
      clickedFrom,
    })
  }

  const handleImageClick = (index: number) => {
    setCurrentImage(index)
    setShowPopup(true)

    if (images?.length && images[index]) {
      trackImageEvent({ action: 'Clicked', position: index + 1, imageUrl: images[index], clickedFrom })
    }
  }

  const handleCarouselImageClicks = (index: number) => {
    setCurrentImage(index)
    if (images?.length && images[index]) {
      trackImageEvent({ action: 'Clicked', position: index + 1, imageUrl: images[index], clickedFrom })
    }
  }

  if (!images || images.length === 0) return null

  const displayed = images.slice(0, 5)

  return (
    <Wrapper data-testid="product-gallery">
      <Grid count={images.length}>
        {displayed.map((image, index) => (
          <div
            style={{ backgroundImage: `url(${image})` }}
            key={image}
            onClick={() => handleImageClick(index)}
            data-testid="product-gallery-image"
          />
        ))}
      </Grid>
      {/* TODO  Potentially replace with swiper carousel*/}
      {/* This section is for Mobile */}
      <Carousel>
        <Left
          className="prev"
          data-testid="previous-carousel-image"
          clipRule="evenodd"
          fillRule="evenodd"
          strokeLinejoin="round"
          strokeMiterlimit="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m12.012 2c5.518 0 9.997 4.48 9.997 9.998 0 5.517-4.479 9.997-9.997 9.997s-9.998-4.48-9.998-9.997c0-5.518 4.48-9.998 9.998-9.998zm-1.523 6.21s-1.502 1.505-3.255 3.259c-.147.147-.22.339-.22.531s.073.383.22.53c1.753 1.754 3.254 3.258 3.254 3.258.145.145.335.217.526.217.192-.001.384-.074.531-.221.292-.293.294-.766.003-1.057l-1.977-1.977h6.693c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-6.693l1.978-1.979c.29-.289.287-.762-.006-1.054-.147-.147-.339-.221-.53-.222-.19 0-.38.071-.524.215z"
            fillRule="nonzero"
          />
        </Left>
        <Swiper
          loop={images.length > 3}
          enabled={images.length > 1}
          pagination
          onSlideChange={(i) => {
            setCurrentImage((prevIndex: number) => {
              if (prevIndex !== i.realIndex) {
                handleCarouselImageClicks(i.realIndex)
              }

              return i.realIndex
            })
          }}
          navigation={{ prevEl: '.prev', nextEl: '.next' }}
          modules={[Navigation, Pagination]}
        >
          <Grid count={images.length}>
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                <ProductImage
                  style={{ backgroundImage: `url(${image})` }}
                  data-testid="product-gallery--carousel-image"
                />
              </SwiperSlide>
            ))}
          </Grid>
        </Swiper>
        <Right
          className="next"
          data-testid="next-carousel-image"
          clipRule="evenodd"
          fillRule="evenodd"
          strokeLinejoin="round"
          strokeMiterlimit="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m12.007 2c-5.518 0-9.998 4.48-9.998 9.998 0 5.517 4.48 9.997 9.998 9.997s9.998-4.48 9.998-9.997c0-5.518-4.48-9.998-9.998-9.998zm1.523 6.21s1.502 1.505 3.255 3.259c.147.147.22.339.22.531s-.073.383-.22.53c-1.753 1.754-3.254 3.258-3.254 3.258-.145.145-.335.217-.526.217-.192-.001-.384-.074-.531-.221-.292-.293-.294-.766-.003-1.057l1.977-1.977h-6.693c-.414 0-.75-.336-.75-.75s.336-.75.75-.75h6.693l-1.978-1.979c-.29-.289-.287-.762.006-1.054.147-.147.339-.221.53-.222.19 0 .38.071.524.215z"
            fillRule="nonzero"
          />
        </Right>
      </Carousel>

      {images.length > 5 && (
        <More data-testid="product-gallery-more" onClick={() => setShowPopup(true)}>
          See all photos
        </More>
      )}
      {showPopup &&
        createPortal(
          <>
            <Overlay />
            <Close
              clipRule="evenodd"
              fillRule="evenodd"
              strokeLinejoin="round"
              strokeMiterlimit="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => setShowPopup(false)}
              data-testid="close-popup-image"
            >
              <path
                d="m12.002 2.005c5.518 0 9.998 4.48 9.998 9.997 0 5.518-4.48 9.998-9.998 9.998-5.517 0-9.997-4.48-9.997-9.998 0-5.517 4.48-9.997 9.997-9.997zm0 1.5c-4.69 0-8.497 3.807-8.497 8.497s3.807 8.498 8.497 8.498 8.498-3.808 8.498-8.498-3.808-8.497-8.498-8.497zm0 7.425 2.717-2.718c.146-.146.339-.219.531-.219.404 0 .75.325.75.75 0 .193-.073.384-.219.531l-2.717 2.717 2.727 2.728c.147.147.22.339.22.531 0 .427-.349.75-.75.75-.192 0-.384-.073-.53-.219l-2.729-2.728-2.728 2.728c-.146.146-.338.219-.53.219-.401 0-.751-.323-.751-.75 0-.192.073-.384.22-.531l2.728-2.728-2.722-2.722c-.146-.147-.219-.338-.219-.531 0-.425.346-.749.75-.749.192 0 .385.073.531.219z"
                fillRule="nonzero"
              />
            </Close>
            <Left
              clipRule="evenodd"
              fillRule="evenodd"
              strokeLinejoin="round"
              strokeMiterlimit="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => handleCarouselImageClicks(currentImage === 0 ? images!.length - 1 : currentImage! - 1)}
              data-testid="previous-popup-image"
            >
              <path
                d="m12.012 2c5.518 0 9.997 4.48 9.997 9.998 0 5.517-4.479 9.997-9.997 9.997s-9.998-4.48-9.998-9.997c0-5.518 4.48-9.998 9.998-9.998zm-1.523 6.21s-1.502 1.505-3.255 3.259c-.147.147-.22.339-.22.531s.073.383.22.53c1.753 1.754 3.254 3.258 3.254 3.258.145.145.335.217.526.217.192-.001.384-.074.531-.221.292-.293.294-.766.003-1.057l-1.977-1.977h6.693c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-6.693l1.978-1.979c.29-.289.287-.762-.006-1.054-.147-.147-.339-.221-.53-.222-.19 0-.38.071-.524.215z"
                fillRule="nonzero"
              />
            </Left>
            <PopupImage
              src={images[currentImage!]}
              data-testid="popup-image"
              onLoad={handleImageFullyLoaded}
              onError={handleImageLoadError}
            />
            <Right
              clipRule="evenodd"
              fillRule="evenodd"
              strokeLinejoin="round"
              strokeMiterlimit="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => handleCarouselImageClicks(currentImage === images!.length - 1 ? 0 : currentImage! + 1)}
              data-testid="next-popup-image"
            >
              <path
                d="m12.007 2c-5.518 0-9.998 4.48-9.998 9.998 0 5.517 4.48 9.997 9.998 9.997s9.998-4.48 9.998-9.997c0-5.518-4.48-9.998-9.998-9.998zm1.523 6.21s1.502 1.505 3.255 3.259c.147.147.22.339.22.531s-.073.383-.22.53c-1.753 1.754-3.254 3.258-3.254 3.258-.145.145-.335.217-.526.217-.192-.001-.384-.074-.531-.221-.292-.293-.294-.766-.003-1.057l1.977-1.977h-6.693c-.414 0-.75-.336-.75-.75s.336-.75.75-.75h6.693l-1.978-1.979c-.29-.289-.287-.762.006-1.054.147-.147.339-.221.53-.222.19 0 .38.071.524.215z"
                fillRule="nonzero"
              />
            </Right>
          </>,
          document.body
        )}
    </Wrapper>
  )
}

export default ProductGallery
